/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Editor } from '@tinymce/tinymce-react';
import './TinyEditor.scss';
import sanitizeHtml from 'sanitize-html';

// https://github.com/tinymce/tinymce-react/issues/23
// Import TinyMCE and theme
import 'tinymce';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';

// Import skin
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';

// Import plugins
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/paste';
import 'tinymce/plugins/image';
import 'tinymce/plugins/imagetools';
import 'tinymce/plugins/charmap';
import { countWords } from '../../core/helpers/word-count';
import { BASE_URL } from '../../core/config/config';

interface ITinyEditor {
  initialValue: any;
  disabled: any;
  wordLimit: any;
  formatLevel: any;
  handleEditorChange: any;
}

function TinyEditor({
  initialValue,
  disabled,
  wordLimit,
  formatLevel,
  handleEditorChange,
}: ITinyEditor): JSX.Element {
  const sanitizeContent = (content: string) => {
    const sanitizeConfig = {
      allowedTags: [],
      allowedAttributes: {},
    };
    return sanitizeHtml(content, sanitizeConfig);
  };

  const handleKeyDown = (event: any, editorRef: any) => {
    if (wordLimit) {
      if (
        countWords(sanitizeContent(editorRef.getContent())) >= wordLimit &&
        (event.code === 'Space' || event.code === 'Enter')
      ) {
        event.preventDefault();
      }
    }
  };

  const toolbars: { [key: number]: string[] } = {
    0: [
      `undo redo | styleselect fontsizeselect | subscript superscript | arrowUp arrowDown arrowRight | forecolor backcolor`,
      `bold italic underline strikethrough | bullist numlist |
     alignleft aligncenter alignright alignjustify outdent indent | removeformat link charmap image`,
    ],
    5: ['undo redo | bold italic underline | alignleft aligncenter alignright | removeformat'],
    10: [],
  };

  return (
    <div className="TinyEditor">
      <Editor
        value={initialValue}
        inline
        disabled={disabled}
        init={{
          height: 500,
          menubar: false,
          plugins: ['advlist lists link paste image charmap imagetools'],
          toolbar: toolbars[formatLevel],
          imagetools_cors_hosts: [BASE_URL.replace('https://', '')],
          default_link_target: '_blank',
          link_assume_external_targets: 'https',
          link_default_protocol: 'https',
          paste_preprocess: (plugin: any, args: { content: string }) => {
            // eslint-disable-next-line no-param-reassign
            args.content = sanitizeContent(args.content);
          },
          extended_valid_elements: 'i[class]',
          // image upload
          // images_upload_url: 'uploadimage',
          // images_upload_base_path: '/some/basepath',
          // images_upload_credentials: true,
          // https://github.com/tinymce/tinymce-react/issues/53
          skin: false,
          color_cols: 6,
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          color_map: getColorMap(),
          // https://codepen.io/tinymce/pen/QjzgRW
          style_formats: [
            { title: 'Paragraph', format: 'p' },
            { title: 'Heading 1', format: 'h1' },
            { title: 'Heading 2', format: 'h2' },
            { title: 'Heading 3', format: 'h3' },
            { title: 'Heading 4', format: 'h4' },
            { title: 'Heading 5', format: 'h5' },
            { title: 'Heading 6', format: 'h6' },
            { title: 'Two columns', format: 'twoColumns' },
            { title: 'Three columns', format: 'threeColumns' },
          ],
          formats: {
            twoColumns: {
              block: 'div',
              wrapper: true,
              styles: { columnCount: '2' },
            },
            threeColumns: {
              block: 'div',
              wrapper: true,
              styles: { columnCount: '3' },
            },
          },
          charmap_append: [
            [0x25b2, 'up pointing triangle'],
            [0x25bc, 'down pointing triangle'],
            [0x25ba, 'right pointing triangle'],
            [0x25c4, 'left pointing triangle'],
            [0x2615, 'morning coffee'],
          ],
          setup: (editor) => {
            editor.ui.registry.addButton('arrowUp', {
              text: '↑',
              tooltip: 'Arrow up',
              onAction: () => {
                editor.insertContent(
                  // `<span contenteditable="false" class="tiny-arrow tiny-arrow-up">&#10140;</span>`
                  '<i contenteditable="false" class="fas fa-long-arrow-alt-up tiny-arrow-up"></i>'
                );
              },
            });
            editor.ui.registry.addButton('arrowDown', {
              text: '↓',
              tooltip: 'Arrow down',
              onAction: () => {
                editor.insertContent(
                  // `<span contenteditable="false" class="tiny-arrow tiny-arrow-down">&#10140;</span>`
                  '<i contenteditable="false" class="fas fa-long-arrow-alt-down tiny-arrow-down"></i>'
                );
              },
            });
            editor.ui.registry.addButton('arrowRight', {
              text: '→',
              tooltip: 'Arrow right',
              onAction: () => {
                editor.insertContent(
                  // `<span contenteditable="false" class="tiny-arrow tiny-arrow-down">&#10140;</span>`
                  '<i contenteditable="false" class="fas fa-long-arrow-alt-right tiny-arrow-right"></i>'
                );
              },
            });
          },
        }}
        onEditorChange={(content) => {
          handleEditorChange(content);
        }}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}

export default TinyEditor;

export const TABLE_CELL_FORMAT_LEVEL = 5;

function getColorMap() {
  return [
    '#E20030',
    'Red',
    '#FF8819',
    'Orange',
    '#710012',
    'Strategy-3',
    '#a100ff',
    'Consulting-1',
    '#7500c0',
    'Consulting-2',
    '#460073',
    'Consulting-3',
    '#ffff00',
    'Digital-1',
    '#ffd42e',
    'Digital-2',
    '#ffb600',
    'Digital-3',
    '#00ff00',
    'Technology-1',
    '#00d700',
    'Technology-2',
    '#00530a',
    'Technology-3',
    '#00baff',
    'Operations-1',
    '#008eff',
    'Operations-2',
    '#004dff',
    'Operations-3',
    '#ff8501',
    'Security-1',
    '#f26722',
    'Security-2',
    '#ae132a',
    'Security-3',
    '#bebebe',
    'Secondary-1',
    '#969696',
    'Secondary-2',
    '#5a5a5a',
    'Secondary-3',
    '#000000',
    'Black',
    '#ffffff',
    'White',
  ];
}
